import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Building } from '@/reducers/types'

import { PlaceFeatureGroup, PlaceFeatureKey } from '../../features/places-features.constants'
import {
  PlaceFeatureCollapsibleGroup,
  PlaceFeatureCollapsibleGroupSkeleton,
} from './place-feature-collapsible-group'
import * as S from './place-feature-collapsible-group.styled'
import { PlaceFeaturesOverview, PlaceFeaturesOverviewSkeleton } from './place-features-overview'

interface PlaceFeatureGroupedListProps {
  building: Building
  // Note: groups shown as one card for the root and one card per provider branch
  branchGroups?: readonly PlaceFeatureGroup[]
  customLabels?: Partial<Record<PlaceFeatureKey, string>>
  errorFeatures?: string[]
  errorMessage?: string
  groups: readonly PlaceFeatureGroup[]
  isAdmin: boolean
  isEditing: boolean
  openGroups: readonly PlaceFeatureGroup[]
  onToggleGroup: (group: PlaceFeatureGroup) => void
}

export const PlaceFeatureGroupedList = ({
  branchGroups = [],
  building,
  customLabels,
  errorFeatures,
  errorMessage,
  groups,
  isAdmin,
  isEditing,
  openGroups,
  onToggleGroup,
}: PlaceFeatureGroupedListProps) => (
  <S.Content>
    <PlaceFeaturesOverview groups={groups} />
    <S.List>
      {groups.map((group) => (
        <PlaceFeatureCollapsibleGroup
          key={group}
          building={building}
          customLabels={customLabels}
          errorFeatures={errorFeatures}
          errorMessage={errorMessage}
          group={group}
          isAdmin={isAdmin}
          isEditing={isEditing}
          isOpen={openGroups.includes(group)}
          splitByBranch={branchGroups.includes(group)}
          onToggle={() => onToggleGroup(group)}
        />
      ))}
    </S.List>
  </S.Content>
)

export const PlaceFeatureGroupedListSkeleton = ({
  groups,
}: {
  groups: readonly PlaceFeatureGroup[]
}) => (
  <S.Content>
    <PlaceFeaturesOverviewSkeleton />
    <S.List>
      {groups.map((group) => (
        <PlaceFeatureCollapsibleGroupSkeleton key={group} group={group} />
      ))}
    </S.List>
  </S.Content>
)

export const PlaceFeaturesToggleAll = ({
  areAllOpen,
  onClick,
}: {
  areAllOpen: boolean
  onClick: VoidFunction
}) => {
  const { t } = useTranslation(['organisation'])

  return (
    <Button size="small" use="secondary" onClick={onClick}>
      {areAllOpen
        ? t('organisation:places.features.collapseAll')
        : t('organisation:places.features.expandAll')}
    </Button>
  )
}
